import { FC, Fragment } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { BKJLoadingSpinner, Flex } from '@bookjane2/bookjane-design-library';
import { UserGuard } from 'guards/UserGuard';
import { UserPermissionEnum } from 'guards/UserGuard.types';
import { usePositionsAllocationSettingsPageDataContext } from 'pages/LocationSettingsPage/PositionsAllocation/PositionsAllocationSettingsPage.data.context';
import { H3 } from 'components/Typography';
import {
  PositionsAllocationContentWrapper,
  PositionsAllocationTableBody,
  PositionsAllocationSubHeaderTitle,
  PositionsAllocationEditButton,
  PositionsAllocationNote,
  NoAssignedPositionsMessageTitle,
  NoAssignedPositionsMessage,
  PositionsAllocationTableWrapper,
  PositionsAllocationTableRowWrapper,
  PositionsAllocationSubHeaderTableData,
  PositionsAllocationSubHeaderWrapper,
} from 'pages/LocationSettingsPage/PositionsAllocation/PositionsAllocation.styled';

import { useSelector } from 'react-redux';
import isUserSecondarySelector from 'store/selectors/userSelectors/isUserSecondarySelector';
import { PositionDetails } from 'types/graphql';

export const ReadView: FC = () => {
  const history = useHistory();
  const match = useRouteMatch();
  const { groupedPositions, isLoading } = usePositionsAllocationSettingsPageDataContext();
  const { t } = useTranslation();
  const isReadOnly = useSelector(isUserSecondarySelector);

  return isLoading ? (
    <BKJLoadingSpinner variant={'Default'} />
  ) : (
    <Fragment>
      <Flex justifyContent="space-between" margin="0 0 40px 0" maxWidth="844px">
        <H3>{t('location_settings:LOCATION_POSITIONS')}</H3>
        <UserGuard userType={[UserPermissionEnum.USER_TYPE_B2B]}>
          <PositionsAllocationEditButton
            variant="PurpleOutlined"
            width="192px"
            minWidth="192px"
            onClick={() => {
              history.push(`${match.url}/edit`);
            }}
          >
            {t('location_settings:EDIT')}
          </PositionsAllocationEditButton>
        </UserGuard>
      </Flex>
      <Flex maxWidth="672px">
        <PositionsAllocationContentWrapper>
          <PositionsAllocationNote>
            {t(
              `location_settings:${
                isReadOnly
                  ? 'LOCATION_POSITIONS_PAGE_SECONDARY_USER_NOTE'
                  : 'LOCATION_POSITIONS_PAGE_NOTE'
              }`,
            )}
          </PositionsAllocationNote>
          {Object.keys(groupedPositions).length > 0 ? (
            Object.keys(groupedPositions).map((departmentName, index) => {
              const positions = groupedPositions[departmentName];
              return (
                <Flex key={index} flexDirection="column" margin="32px 0 0 0">
                  <PositionsAllocationTableBody gridTemplateColumns="100%">
                    <PositionsAllocationSubHeaderWrapper>
                      <PositionsAllocationSubHeaderTitle>
                        {departmentName}
                      </PositionsAllocationSubHeaderTitle>
                    </PositionsAllocationSubHeaderWrapper>
                  </PositionsAllocationTableBody>

                  <PositionsAllocationTableWrapper>
                    <PositionsAllocationTableBody gridTemplateColumns="100%">
                      {positions.map((position: PositionDetails, index: number) => {
                        return (
                          <PositionsAllocationTableRowWrapper isGrey={index % 2 === 0} key={index}>
                            <PositionsAllocationSubHeaderTableData>
                              {position.name}
                            </PositionsAllocationSubHeaderTableData>
                          </PositionsAllocationTableRowWrapper>
                        );
                      })}
                    </PositionsAllocationTableBody>
                  </PositionsAllocationTableWrapper>
                </Flex>
              );
            })
          ) : (
            <Flex
              justifyContent="space-between"
              margin="88px auto 0 auto"
              flexDirection="column"
              alignContent="center"
              gap="12px"
            >
              <NoAssignedPositionsMessageTitle>
                {t('location_settings:NO_ASSIGNED_POSITIONS_MESSAGE_TITLE')}
              </NoAssignedPositionsMessageTitle>
              <NoAssignedPositionsMessage>
                {!isReadOnly && t('location_settings:POSITIONS_ALLOCATION_EDIT_PAGE')}
              </NoAssignedPositionsMessage>
            </Flex>
          )}
        </PositionsAllocationContentWrapper>
      </Flex>
    </Fragment>
  );
};
